/* tslint:disable: ban-types*/
import { difference, keys } from 'lodash';
import createSlangContext from 'x-slang/dist/createContext';
import { Context, Variant } from 'x-slang/dist/types';
import { stringify } from 'x-slang/dist/utils/stringify';

import { handleConsoleLog } from '../application/actions/InterpreterActions';
import Constants from './Constants';

/**
 * This file contains wrappers for certain functions
 * in the @source-academy/slang module.
 *
 * Use this file especially when attempting to create a slang Context.
 */

/**
 * Function that takes a value and displays it in the interpreter.
 * The value is displayed however native JS would convert it to a string.
 * e.g. [1, 2, 3] would be displayed as 1,2,3.
 * An action is dispatched using the redux store reference
 * within the global window object.
 *
 * @param str the value to be displayed
 * @param workspaceLocation used to determine
 *   which REPL the value shows up in.
 */
function rawDisplay(str: string, prepend: string, workspaceLocation: any) {
  const output = (prepend === undefined ? '' : prepend + ' ') + str;
  // TODO in 2019: fix this hack
  if (typeof (window as any).__REDUX_STORE__ !== 'undefined') {
    (window as any).__REDUX_STORE__.dispatch(handleConsoleLog(output, workspaceLocation));
  }
}

export function visualiseEnv(context: Context) {
  if ((window as any).EnvVisualizer) {
    (window as any).EnvVisualizer.draw_env({ context });
  } else {
    throw new Error('Env visualizer is not enabled');
  }
}

export function highlightLine(line: number | undefined) {
  if ((window as any).Inspector) {
    (window as any).Inspector.highlightClean();
    // if number is undefined it just clears the highlighting.
    (window as any).Inspector.highlightLine(line);
  } else {
    throw new Error('Inspector not loaded');
  }
}

export function inspectorUpdate(context: Context | undefined) {
  if ((window as any).Inspector) {
    (window as any).Inspector.updateContext(context, stringify);
  } else {
    throw new Error('Inspector not loaded');
  }
}

export const externalBuiltIns = {
  rawDisplay
};

/**
 * A wrapper around x-slang's createContext. This
 * provides the original function with the required
 * externalBuiltIns, such as display and prompt.
 */
export function createContext<T>(
  externals: string[],
  externalContext: T,
  variant: Variant = Constants.defaultSourceVariant,
  moduleParams?: any
) {
  return createSlangContext<T>(variant, externals, externalContext, externalBuiltIns, moduleParams);
}

// Given a Context, returns a privileged Context that when referenced,
// intercepts reads from the underlying Context and returns desired values
export function makeElevatedContext(context: Context) {
  function ProxyFrame() {}
  ProxyFrame.prototype = context.runtime.environments[0].head;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const fakeFrame: { [key: string]: any } = new ProxyFrame();
  // Explanation: Proxy doesn't work for defineProperty in use-strict.
  // The x-slang will defineProperty on loadStandardLibraries
  // Creating a raw JS object and setting prototype will allow defineProperty on the child
  // while reflection should work on parent.

  const proxyGlobalEnv = new Proxy(context.runtime.environments[0], {
    get(target, prop: string | number, receiver) {
      if (prop === 'head') {
        return fakeFrame;
      }
      return target[prop];
    }
  });

  const proxyEnvs = new Proxy(context.runtime.environments, {
    get(target, prop, receiver) {
      if (prop === '0') {
        return proxyGlobalEnv;
      }
      return target[prop];
    }
  });

  const proxyRuntime = new Proxy(context.runtime, {
    get(target, prop, receiver) {
      if (prop === 'environments') {
        return proxyEnvs;
      }
      return target[prop];
    }
  });

  const elevatedContext = new Proxy(context, {
    get(target, prop, receiver) {
      switch (prop) {
        case 'variant':
          return 4;
        case 'runtime':
          return proxyRuntime;
        default:
          return target[prop];
      }
    }
  });
  return elevatedContext;
}

export function getDifferenceInMethods(elevatedContext: Context, context: Context) {
  const eFrame = elevatedContext.runtime.environments[0].head;
  const frame = context.runtime.environments[0].head;
  return difference(keys(eFrame), keys(frame));
}

export function getStoreExtraMethodsString(toRemove: string[], unblockKey: string) {
  return `const _____${unblockKey} = [${toRemove.join(', ')}];`;
}

export function getRestoreExtraMethodsString(removed: string[], unblockKey: string) {
  const store = `_____${unblockKey}`;
  return removed
    .map((x, key) => (x === 'makeUndefinedErrorFunction' ? '' : `const ${x} = ${store}[${key}];`))
    .join('\n');
}

export function getBlockExtraMethodsString(toRemove: string[]) {
  return toRemove
    .map(x =>
      x === 'makeUndefinedErrorFunction' ? '' : `const ${x} = makeUndefinedErrorFunction('${x}');`
    )
    .join('\n');
}
